import {useSearchParams} from "react-router-dom";
import {SiteLink as Link} from "nav/SiteLink";
import {useMessages} from "i18n";
import {useUnsubscribe} from "./useUnsubscribe";
import {canConfirm} from "./unsubscribeState";
import type {UnsubscribeScope} from "./unsubscribeScope";
import {Actions, Body, Card, ConfirmButton, Error, Heading, ManageLink, Root, Scope} from "./Unsubscribe.styles";

/**
 * Anonymous opt-out page (ui#316) for the unsubscribe link in recipient-facing emails.
 *
 * Dumb view over {@link ./useUnsubscribe}: it renders the current state and nothing else — the
 * transitions live in {@link ./unsubscribeState}. The confirmation step is deliberate rather than a
 * bare link target, because mail scanners and prefetchers follow links unprompted and must not be able
 * to unsubscribe somebody by fetching the page.
 */
export function Unsubscribe({scope = "transfers"}: {scope?: UnsubscribeScope}) {
	const messages = useMessages();
	// Scope picks the WORDING SET, not a reworded shared one — the recipient copy says the opt-out
	// applies to every sender and stops files arriving, which would badly misdescribe what a
	// marketing unsubscribe does. See the `marketing` block in the catalog.
	const copy = scope === "marketing"
		? {...messages.unsubscribe, ...messages.unsubscribe.marketing}
		: messages.unsubscribe;
	// The merged `copy` is a union, so `manage` is not visible on it outside the marketing branch;
	// this names the one namespace that has it, and it is read only where scope is marketing.
	const manageLabel = messages.unsubscribe.marketing.manage;
	const [searchParams] = useSearchParams();
	const {state, failed, confirm} = useUnsubscribe(searchParams.get("token"), scope);

	return (
		<Root>
			<Card>
				{state.kind === "loading" && <Body>{copy.loading}</Body>}

				{state.kind === "invalid" && (
					<>
						<Heading>{copy.invalidHeading}</Heading>
						<Body>{copy.invalidBody}</Body>
					</>
				)}

				{state.kind === "already" && (
					<>
						<Heading>{copy.alreadyHeading}</Heading>
						<Body>{copy.alreadyBody(state.email)}</Body>
						{scope === "marketing" && (
							<ManageLink as={Link} to="/account">
								{manageLabel}
							</ManageLink>
						)}
					</>
				)}

				{state.kind === "done" && (
					<>
						<Heading>{copy.doneHeading}</Heading>
						<Body>{copy.doneBody(state.email)}</Body>
						{scope === "marketing" && (
							<ManageLink as={Link} to="/account">
								{manageLabel}
							</ManageLink>
						)}
					</>
				)}

				{(state.kind === "confirming" || state.kind === "submitting") && (
					<>
						<Heading>{copy.heading}</Heading>
						<Body>{copy.intro(state.email)}</Body>
						{/* The marketing scope's line is a REASSURANCE ("your receipts still arrive") and gets
						    the positive band. The recipient's is the opposite — it warns that every sender is
						    blocked — so it stays plain body text; painting it green would present an
						    irreversible consequence as good news. */}
						{scope === "marketing"
							? <Scope>{copy.scope}</Scope>
							: <Body>{copy.scope}</Body>}
						<Actions>
							<ConfirmButton type="button" onClick={confirm} disabled={!canConfirm(state)}>
								{state.kind === "submitting" ? copy.submitting : copy.confirm}
							</ConfirmButton>
						</Actions>
						{failed && <Error role="alert">{copy.failed}</Error>}
						{/* Deliberately the ONLY account state this page offers. The token arrives in an
						    email that can be forwarded, so anything shown here is shown to whoever holds
						    the link; the real preferences live behind a login. */}
						{scope === "marketing" && (
							<ManageLink as={Link} to="/account">
								{manageLabel}
							</ManageLink>
						)}
					</>
				)}
			</Card>
		</Root>
	);
}
